import { useEffect, useState } from 'react'

// Set as blob metadata by the AI image-edit flow at upload time; its presence
// marks the blob as AI generated (the CMS asset itself has no such flag).
export const AI_GENERATED_META_KEY = 'x-ms-meta-ai_request_id'

// In-flight and completed HEAD requests keyed by blob URL, so surfaces showing
// the same blob (asset card, asset settings, version list) share one request
// and always agree on the result.
const metadataCache = new Map()

const fetchBlobMetadata = (blobUrl) => {
  if (!metadataCache.has(blobUrl)) {
    metadataCache.set(blobUrl, new Promise((resolve) => {
      // The 'chmeta' param keeps this request's HTTP-cache key distinct from a
      // full-size <img> fetch of the same URL. Chrome caches that no-CORS image
      // response WITHOUT CORS headers (Azure only sends them when Origin is
      // present) and, while the entry is heuristically fresh, answers a
      // same-URL XHR from it — the XHR then fails its CORS check and reads as
      // "no metadata". Azure ignores unknown query params, on SAS URLs too.
      const requestUrl = `${blobUrl}${blobUrl.includes('?') ? '&' : '?'}chmeta=1`
      const xhr = new XMLHttpRequest()
      xhr.open('HEAD', requestUrl)
      xhr.timeout = 15000
      xhr.onloadend = () => {
        if (xhr.status === 200) {
          const meta = {}
          xhr.getAllResponseHeaders()
            .trim()
            .split(/[\r\n]+/)
            .map(value => value.split(/: /))
            .forEach((keyValue) => {
              if (keyValue[0].includes('x-ms-meta')) {
                meta[keyValue[0].trim()] = keyValue[1].trim()
              }
            })
          resolve(meta)
        } else {
          metadataCache.delete(blobUrl) // failed — let a later mount retry
          resolve({})
        }
      }
      xhr.send(null)
    }))
  }
  return metadataCache.get(blobUrl)
}

// Builds the same blob URL GridItem passes to useBlobMetadata, so both share the
// one cached HEAD request per asset.
const contentBlobUrl = content => `${content.file?.downloadUrl || content.assetUrl}`

// Resolves which of the given contents are AI generated, via the shared HEAD-
// request cache. Returns { aiContentIds, loading } — the Set grows as requests
// resolve, and loading stays true until every HEAD for the current asset set
// has landed. The AI marker is blob metadata, so it can only be known after the
// HEAD lands — the same reason GridItem discovers it asynchronously.
export const useAiGeneratedContentIds = (contents) => {
  const [aiContentIds, setAiContentIds] = useState(() => new Set())
  const [loading, setLoading] = useState(false)

  // Re-run only when the set of asset urls changes, not on every render.
  const urlsKey = contents.map(contentBlobUrl).join('|')

  useEffect(() => {
    let active = true

    const withUrl = contents.filter(content => content.file?.downloadUrl || content.assetUrl)

    if (!withUrl.length) {
      setAiContentIds(new Set())
      setLoading(false)
      return undefined
    }

    setLoading(true)

    Promise.all(withUrl.map(content => fetchBlobMetadata(contentBlobUrl(content)).then(meta => ({
      id: content.id,
      isAi: Boolean(meta[AI_GENERATED_META_KEY]),
    })))).then((results) => {
      if (!active) return
      setAiContentIds(new Set(results.filter(result => result.isAi).map(result => result.id)))
      setLoading(false)
    })

    return () => { active = false }
  }, [urlsKey])

  return { aiContentIds, loading }
}

// Reads a blob's x-ms-meta-* metadata via a HEAD request against its (SAS /
// public) URL. Storage CORS already permits this — same approach as
// Gallery/GridView/GridItem and the resources ListView.
const useBlobMetadata = (blobUrl) => {
  const [metadata, setMetadata] = useState({})

  useEffect(() => {
    let active = true
    setMetadata({})
    if (!blobUrl) return undefined

    // No abort on unmount — the request is shared with other consumers.
    fetchBlobMetadata(blobUrl).then((meta) => {
      if (active) setMetadata(meta)
    })

    return () => { active = false }
  }, [blobUrl])

  return metadata
}

export default useBlobMetadata
